import { useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useLogto } from '@logto/react'
import { isLogtoEnabled } from '@/providers/LogtoProviderWrapper'
import { useAuthStore } from '@/store/useAuthStore'

// Login button that handles both Logto and dev mode
function LogtoLoginButton({ className, children }: { className: string; children: React.ReactNode }) {
  const { signIn } = useLogto()
  return (
    <button className={className} onClick={() => signIn(`${window.location.origin}/app/callback`)}>
      {children}
    </button>
  )
}

function DevLoginButton({ className, children }: { className: string; children: React.ReactNode }) {
  const navigate = useNavigate()
  const { setDevMode } = useAuthStore()
  return (
    <button className={className} onClick={() => { setDevMode(); navigate('/app/prompts', { replace: true }) }}>
      {children}
    </button>
  )
}

export function LoginButton({ className, children }: { className: string; children: React.ReactNode }) {
  return isLogtoEnabled()
    ? <LogtoLoginButton className={className}>{children}</LogtoLoginButton>
    : <DevLoginButton className={className}>{children}</DevLoginButton>
}

// ---- Official brand SVG icons ----
const ChatGPTIcon = ({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg viewBox="0 0 24 24" width={size} height={size}>
    <path fill={color} d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.778-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.12 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zM8.306 12.863l-2.02-1.164a.08.08 0 0 1-.038-.057V6.074a4.5 4.5 0 0 1 7.376-3.454l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.098-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
  </svg>
)

const ClaudeIcon = ({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg viewBox="0 -.01 39.5 39.53" width={size} height={size} fill="none">
    <path fill={color} d="m7.75 26.27 7.77-4.36.13-.38-.13-.21h-.38l-1.3-.08-4.44-.12-3.85-.16-3.73-.2-.94-.2-.88-1.16.09-.58.79-.53 1.13.1 2.5.17 3.75.26 2.72.16 4.03.42h.64l.09-.26-.22-.16-.17-.16-3.88-2.63-4.2-2.78-2.2-1.6-1.19-.81-.6-.76-.26-1.66 1.08-1.19 1.45.1.37.1 1.47 1.13 3.14 2.43 4.1 3.02.6.5.24-.17.03-.12-.27-.45-2.23-4.03-2.38-4.1-1.06-1.7-.28-1.02c-.1-.42-.17-.77-.17-1.2l1.23-1.67.68-.22 1.64.22.69.6 1.02 2.33 1.65 3.67 2.56 4.99.75 1.48.4 1.37.15.42h.26v-.24l.21-2.81.39-3.45.38-4.44.13-1.25.62-1.5 1.23-.81.96.46.79 1.13-.11.73-.47 3.05-.92 4.78-.6 3.2h.35l.4-.4 1.62-2.15 2.72-3.4 1.2-1.35 1.4-1.49.9-.71h1.7l1.25 1.86-.56 1.92-1.75 2.22-1.45 1.88-2.08 2.8-1.3 2.24.12.18.31-.03 4.7-1 2.54-.46 3.03-.52 1.37.64.15.65-.54 1.33-3.24.8-3.8.76-5.66 1.34-.07.05.08.1 2.55.24 1.09.06h2.67l4.97.37 1.3.86.78 1.05-.13.8-2 1.02-2.7-.64-6.3-1.5-2.16-.54h-.3v.18l1.8 1.76 3.3 2.98 4.13 3.84.21.95-.53.75-.56-.08-3.63-2.73-1.4-1.23-3.17-2.67h-.21v.28l.73 1.07 3.86 5.8.2 1.78-.28.58-1 .35-1.1-.2-2.26-3.17-2.33-3.57-1.88-3.2-.23.13-1.11 11.95-.52.61-1.2.46-1-.76-.53-1.23.53-2.43.64-3.17.52-2.52.47-3.13.28-1.04-.02-.07-.23.03-2.36 3.24-3.59 4.85-2.84 3.04-.68.27-1.18-.61.11-1.09.66-.97 3.93-5 2.37-3.1 1.53-1.79-.01-.26h-.09l-10.44 6.78-1.86.24-.8-.75.1-1.23.38-.4 3.14-2.16z" />
  </svg>
)

const GeminiIcon = ({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg viewBox="0 0 24 24" width={size} height={size}>
    <path fill={color} d="M12 0C12 6.627 6.627 12 0 12c6.627 0 12 5.373 12 12 0-6.627 5.373-12 12-12-6.627 0-12-5.373-12-12Z" />
  </svg>
)

// ---- FAQ data ----
const faqItems = [
  {
    question: 'What is Generative Engine Optimization (GEO)?',
    answer:
      'GEO is a new discipline focused on optimizing your content to be cited and recommended by AI-powered search engines like ChatGPT, Claude, and Gemini. Unlike traditional SEO which targets Google\'s ranking algorithm, GEO ensures your brand is visible when users ask AI assistants for recommendations.',
  },
  {
    question: 'How does GEO Writer track brand visibility?',
    answer:
      'GEO Writer sends your target prompts to multiple AI platforms and analyzes the responses. It checks whether your brand is mentioned, how prominently it appears, and tracks these metrics over time so you can see trends and measure improvement.',
  },
  {
    question: 'Which AI platforms are supported?',
    answer:
      'We currently support ChatGPT (OpenAI), Claude (Anthropic), and Gemini (Google). We continuously evaluate and add new platforms as the AI search landscape evolves.',
  },
  {
    question: 'How is GEO different from traditional SEO?',
    answer:
      'Traditional SEO optimizes for search engine result pages (SERPs) and ranking algorithms. GEO optimizes for AI language models that generate answers from their training data and real-time retrieval. The techniques differ — GEO focuses on structured data, authoritative citations, and content patterns that AI models prefer to reference.',
  },
  {
    question: 'How quickly can I see results?',
    answer:
      'You can start tracking your brand visibility immediately after setup. Content optimization results depend on how AI models update their knowledge, but many users see measurable improvements in citation rates within weeks of publishing GEO-optimized content.',
  },
]

// ---- FAQ Item Component ----
const FaqItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className={`lp-faq-item ${isOpen ? 'lp-faq-item-open' : ''}`}>
      <button className="lp-faq-question" onClick={() => setIsOpen(!isOpen)}>
        <span>{question}</span>
        <svg
          className="lp-faq-chevron"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div className="lp-faq-answer">
        <p>{answer}</p>
      </div>
    </div>
  )
}

/**
 * GEO Writer Landing Page
 * Cursor-inspired minimal design with light theme
 */
export const LandingPage = () => {
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Intersection Observer for scroll animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('lp-visible')
          }
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    )

    const elements = document.querySelectorAll('.lp-animate')
    elements.forEach((el) => observerRef.current?.observe(el))

    return () => observerRef.current?.disconnect()
  }, [])

  return (
    <div className="lp-root">
      {/* Navigation */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <div className="lp-nav-logo">
            <span>GEO Writer</span>
            <span className="lp-nav-logo-dot">.</span>
          </div>
          <div className="lp-nav-links">
            <a href="#features" className="lp-nav-link">Features</a>
            <a href="#how-it-works" className="lp-nav-link">How It Works</a>
            <a href="#platforms" className="lp-nav-link">Platforms</a>
            <a href="#faq" className="lp-nav-link">FAQ</a>
            <a href="/blog" className="lp-nav-link">Blog</a>
          </div>
          <div className="lp-nav-actions">
            <LoginButton className="lp-nav-signin">Sign In</LoginButton>
            <LoginButton className="lp-nav-cta">Get Started</LoginButton>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="lp-hero lp-hero-stacked">
        <div className="lp-hero-content lp-hero-content-center">
          <div className="lp-hero-badge lp-animate">
            <span className="lp-hero-badge-dot" />
            Generative Engine Optimization
          </div>
          <h1 className="lp-hero-title lp-animate">
            AI-optimized content<br />
            <span className="lp-hero-title-accent">that gets your brand cited</span>
          </h1>
          <p className="lp-hero-subtitle lp-animate">
            Monitor your visibility across ChatGPT, Claude, Perplexity, Gemini.
            Create content engineered to be cited with our skill-based, context-aware writer agent.
          </p>
          <div className="lp-hero-actions lp-hero-actions-center lp-animate">
            <LoginButton className="lp-btn-primary">
              Start Creating
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
              </svg>
            </LoginButton>
            <a
              href="https://discord.com/invite/srRJpbcMjF"
              target="_blank"
              rel="noopener noreferrer"
              className="lp-btn-secondary"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 8 }}>
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
              </svg>
              Join our Discord Community
            </a>
          </div>
        </div>

        {/* Hero visual: Content Studio mockup */}
        <div className="lp-hero-studio lp-animate">
          <div className="lp-studio-card">
            <div className="lp-hero-card-header">
              <div className="lp-hero-card-dots">
                <span /><span /><span />
              </div>
              <span className="lp-hero-card-title">Content Studio</span>
            </div>
            <div className="lp-studio-body">
              {/* Editor preview */}
              <div className="lp-studio-editor">
                <div className="lp-studio-editor-toolbar">
                  <span className="lp-studio-toolbar-item lp-studio-toolbar-active">Edit</span>
                  <span className="lp-studio-toolbar-item">Preview</span>
                  <span className="lp-studio-toolbar-divider" />
                  <span className="lp-studio-toolbar-item">B</span>
                  <span className="lp-studio-toolbar-item">I</span>
                  <span className="lp-studio-toolbar-item">H</span>
                </div>
                <div className="lp-studio-editor-content">
                  <h4>The Complete Guide to SaaS Onboarding Best Practices</h4>
                  <p className="lp-studio-editor-meta">
                    According to <strong>Userpilot research</strong>, companies that implement structured onboarding see a <strong>50% increase</strong> in user activation rates.
                  </p>
                  <p>
                    Effective SaaS onboarding is the single most critical factor in reducing churn and driving long-term retention. Here are the proven strategies...
                  </p>
                  <div className="lp-studio-editor-table">
                    <div className="lp-studio-table-row lp-studio-table-header">
                      <span>Strategy</span><span>Impact</span><span>Difficulty</span>
                    </div>
                    <div className="lp-studio-table-row">
                      <span>Progressive disclosure</span><span>High</span><span>Medium</span>
                    </div>
                    <div className="lp-studio-table-row">
                      <span>Interactive walkthroughs</span><span>High</span><span>Low</span>
                    </div>
                    <div className="lp-studio-table-row">
                      <span>Contextual tooltips</span><span>Medium</span><span>Low</span>
                    </div>
                  </div>
                  <p style={{ marginTop: 20, color: '#888', fontSize: 14 }}>
                    Studies show that users who complete onboarding within the first session are <strong style={{ color: '#444' }}>3x more likely</strong> to convert to paid plans.
                  </p>
                  <p style={{ marginTop: 12 }}>
                    The key insight is that <strong>personalized onboarding flows</strong> outperform generic ones by a significant margin. Tailoring the experience based on user role, company size, and stated goals leads to measurably higher engagement.
                  </p>
                </div>
              </div>

              {/* Right: GEO Analysis panel */}
              <div className="lp-studio-score">
                <div className="lp-studio-active-skills">
                  <span className="lp-studio-section-title">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" /></svg>
                    Active Skills
                  </span>
                  <div className="lp-studio-skill-tags">
                    <span className="lp-studio-skill-dot active">Inverted Pyramid</span>
                    <span className="lp-studio-skill-dot active">Data Tables</span>
                    <span className="lp-studio-skill-dot active">Authority Signals</span>
                    <span className="lp-studio-skill-dot">Contextual Priming</span>
                  </div>
                </div>
                <div className="lp-studio-checklist-header">
                  <span className="lp-studio-section-title">Optimization Checklist</span>
                  <span className="lp-studio-optimize-btn">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" /></svg>
                    Optimize
                  </span>
                </div>
                <div className="lp-studio-checks">
                  <div className="lp-studio-check lp-studio-check-pass">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                    <span>Direct Conclusion First</span>
                  </div>
                  <div className="lp-studio-check lp-studio-check-pass">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                    <span>Data Tables</span>
                  </div>
                  <div className="lp-studio-check lp-studio-check-pass">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                    <span>Verifiable Statistics</span>
                  </div>
                  <div className="lp-studio-check lp-studio-check-pass">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                    <span>Authority Signals</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* GEO Know-How Section */}
      <section className="lp-knowhow" id="knowhow">
        <div className="lp-section-header lp-animate">
          <h2 className="lp-section-title">Built on proven GEO techniques</h2>
          <p className="lp-section-subtitle">
            Every piece of content is engineered with research-backed optimization skills
            that AI models prefer to cite and recommend.
          </p>
        </div>

        <div className="lp-knowhow-grid">
          <div className="lp-knowhow-card lp-animate">
            <div className="lp-knowhow-card-num">01</div>
            <h3 className="lp-knowhow-card-title">Inverted Pyramid Architecture</h3>
            <p className="lp-knowhow-card-desc">
              Front-load critical information so AI models extract and cite your key messages first.
            </p>
          </div>
          <div className="lp-knowhow-card lp-animate">
            <div className="lp-knowhow-card-num">02</div>
            <h3 className="lp-knowhow-card-title">Authoritative Source Embedding</h3>
            <p className="lp-knowhow-card-desc">
              Weave in credible citations, expert quotes, and verified data that AI prioritizes in recommendations.
            </p>
          </div>
          <div className="lp-knowhow-card lp-animate">
            <div className="lp-knowhow-card-num">03</div>
            <h3 className="lp-knowhow-card-title">Structured Data Formatting</h3>
            <p className="lp-knowhow-card-desc">
              Present comparisons and metrics in clean tables that AI can easily parse and reference.
            </p>
          </div>
          <div className="lp-knowhow-card lp-animate">
            <div className="lp-knowhow-card-num">04</div>
            <h3 className="lp-knowhow-card-title">Contextual Narrative Priming</h3>
            <p className="lp-knowhow-card-desc">
              Frame problems so your brand naturally emerges as the logical, AI-recommended solution.
            </p>
          </div>
          <div className="lp-knowhow-card lp-animate">
            <div className="lp-knowhow-card-num">05</div>
            <h3 className="lp-knowhow-card-title">Semantic Density Optimization</h3>
            <p className="lp-knowhow-card-desc">
              Deploy domain-specific vocabulary that AI associates with deep expertise and topical authority.
            </p>
          </div>
          <div className="lp-knowhow-card lp-animate">
            <div className="lp-knowhow-card-num">06</div>
            <h3 className="lp-knowhow-card-title">Direct Answer Engineering</h3>
            <p className="lp-knowhow-card-desc">
              Craft concise, quotable responses that AI models can directly surface to end users.
            </p>
          </div>
        </div>

        <div className="lp-knowhow-more lp-animate">
          <div className="lp-knowhow-more-tags">
            <span className="lp-knowhow-more-tag">Entity Salience</span>
            <span className="lp-knowhow-more-tag">Claim Substantiation</span>
            <span className="lp-knowhow-more-tag">Query Intent Mapping</span>
            <span className="lp-knowhow-more-tag">Topical Clustering</span>
            <span className="lp-knowhow-more-tag">Passage Optimization</span>
            <span className="lp-knowhow-more-tag">Comparative Framing</span>
            <span className="lp-knowhow-more-tag">Attribution Anchoring</span>
            <span className="lp-knowhow-more-tag">Schema Markup Injection</span>
          </div>
          <p className="lp-knowhow-more-text">
            and <strong>20+ more skills</strong> in our growing optimization library
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="lp-features" id="features">
        <div className="lp-section-header lp-animate">
          <h2 className="lp-section-title">Everything you need to optimize for AI</h2>
          <p className="lp-section-subtitle">
            From content generation to performance tracking — a complete toolkit for AI visibility.
          </p>
        </div>

        <div className="lp-features-grid">
          <div className="lp-feature-card lp-animate">
            <div className="lp-feature-icon">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L14.4 9.6L22 12L14.4 14.4L12 22L9.6 14.4L2 12L9.6 9.6L12 2Z" />
              </svg>
            </div>
            <h3 className="lp-feature-title">Content Studio</h3>
            <p className="lp-feature-desc">
              Generate AI-optimized articles with one click. Select your topic, choose GEO skills,
              and produce content engineered to be cited by AI engines.
            </p>
          </div>

          <div className="lp-feature-card lp-animate">
            <div className="lp-feature-icon">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <h3 className="lp-feature-title">Prompt Tracking</h3>
            <p className="lp-feature-desc">
              Monitor whether AI platforms cite your brand when users ask relevant questions.
              Track citation rates across ChatGPT, Claude, and Gemini over time.
            </p>
          </div>

          <div className="lp-feature-card lp-animate">
            <div className="lp-feature-icon">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            </div>
            <h3 className="lp-feature-title">GEO Score</h3>
            <p className="lp-feature-desc">
              Every piece of content is scored against GEO best practices.
              See exactly which optimization checks pass, warn, or need improvement.
            </p>
          </div>

          <div className="lp-feature-card lp-animate">
            <div className="lp-feature-icon">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <h3 className="lp-feature-title">Content Library</h3>
            <p className="lp-feature-desc">
              Manage all optimized content in one place. Track performance,
              iterate, and continuously improve content quality.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="lp-how" id="how-it-works">
        <div className="lp-section-header lp-animate">
          <h2 className="lp-section-title">Get started in three steps</h2>
          <p className="lp-section-subtitle">
            A few simple steps to make your brand stand out in AI search results.
          </p>
        </div>

        <div className="lp-how-steps">
          <div className="lp-how-step lp-animate">
            <div className="lp-how-step-num">01</div>
            <h3 className="lp-how-step-title">Set up your brand</h3>
            <p className="lp-how-step-desc">
              Add your brand name, website, and key descriptions.
              The system will automatically identify your industry and competitors.
            </p>
          </div>
          <div className="lp-how-connector" />
          <div className="lp-how-step lp-animate">
            <div className="lp-how-step-num">02</div>
            <h3 className="lp-how-step-title">Track AI visibility</h3>
            <p className="lp-how-step-desc">
              Add target prompts and the system will automatically query multiple AI platforms
              to monitor your brand citations.
            </p>
          </div>
          <div className="lp-how-connector" />
          <div className="lp-how-step lp-animate">
            <div className="lp-how-step-num">03</div>
            <h3 className="lp-how-step-title">Generate optimized content</h3>
            <p className="lp-how-step-desc">
              Leverage the GEO skills library and AI engine to produce optimized content
              that continuously improves your brand ranking in AI search.
            </p>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="lp-platforms" id="platforms">
        <div className="lp-section-header lp-animate">
          <h2 className="lp-section-title">Coverage across major AI platforms</h2>
          <p className="lp-section-subtitle">
            Comprehensive tracking of your brand performance across all leading AI search engines.
          </p>
        </div>

        <div className="lp-platforms-grid lp-platforms-grid-3 lp-animate">
          <div className="lp-platform-card">
            <div className="lp-platform-card-icon">
              <ChatGPTIcon size={32} color="#10a37f" />
            </div>
            <h4 className="lp-platform-card-name">ChatGPT</h4>
            <p className="lp-platform-card-desc">OpenAI's flagship product, the world's most widely used AI assistant</p>
          </div>
          <div className="lp-platform-card">
            <div className="lp-platform-card-icon">
              <ClaudeIcon size={32} color="#d97757" />
            </div>
            <h4 className="lp-platform-card-name">Claude</h4>
            <p className="lp-platform-card-desc">Anthropic's safe and accurate AI assistant</p>
          </div>
          <div className="lp-platform-card">
            <div className="lp-platform-card-icon">
              <GeminiIcon size={32} color="#4285f4" />
            </div>
            <h4 className="lp-platform-card-name">Gemini</h4>
            <p className="lp-platform-card-desc">Google's next-gen AI model, deeply integrated with search</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="lp-faq" id="faq">
        <div className="lp-section-header lp-animate">
          <h2 className="lp-section-title">Frequently asked questions</h2>
          <p className="lp-section-subtitle">
            Everything you need to know about GEO and how GEO Writer works.
          </p>
        </div>

        <div className="lp-faq-list lp-animate">
          {faqItems.map((item, index) => (
            <FaqItem key={index} question={item.question} answer={item.answer} />
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="lp-cta">
        <div className="lp-cta-content lp-animate">
          <h2 className="lp-cta-title">Start optimizing your AI visibility</h2>
          <p className="lp-cta-subtitle">
            Join GEO Writer and keep your brand ahead in the age of AI search.
          </p>
          <LoginButton className="lp-btn-primary lp-btn-large">
            Get Started Free
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </LoginButton>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <div className="lp-nav-logo">
              <span>GEO Writer</span>
              <span className="lp-nav-logo-dot">.</span>
            </div>
            <p className="lp-footer-tagline">Brand visibility optimization platform for the AI era</p>
          </div>
          <div className="lp-footer-links">
            <div className="lp-footer-col">
              <h5>Product</h5>
              <a href="#features">Features</a>
              <a href="#how-it-works">How It Works</a>
              <a href="#platforms">Platforms</a>
            </div>
            <div className="lp-footer-col">
              <h5>Resources</h5>
              <a href="/blog">Blog</a>
              <a href="#faq">FAQ</a>
              <a href="#features">GEO Skills Library</a>
            </div>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <p>&copy; 2026 Wisflow Technology Limited. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
